import { FileText, Radio } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { socket } from "../mediasoup/socket";
import { getPublicPrejoinCasesApi } from "../../api/publicMeetingJoinApi";
import { formatDisplayDate, getTodayDate } from "../../utils/Date";
import HearingTimer, { getHearingTimes } from "../mediasoup-ui/HearingTimer";
import { recordingSocket } from "../mediasoup/recordingSocket";
import logger from "../../utils/logger";

type StreamStatus =
  | "waiting"
  | "scheduled"
  | "live"
  | "paused"
  | "rec"
  | "ended";

interface CauseInfo {
  cnr?: string;
  petitioner?: string;
  respondent?: string;
}

interface Props {
  courtRoomId: string;
  onActiveCauseChange?: (cause: CauseInfo | null) => void;
  onNeighborsChange?: (neighbors: {
    prev: CauseInfo | null;
    next: CauseInfo | null;
  }) => void;
  onRoomInfoChange?: (info: any) => void;
  streamStatus?: StreamStatus;
  liveCnr?: string;
}

const getId = (value: any) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const getCauseRoomId = (cause: any) => {
  if (!cause) return "";
  return getId(
    cause.courtType === "High Court" ? cause.benchRoom : cause.courtRoom,
  );
};

const getCauseKey = (cause: any) => getId(cause?._id) || cause?.cnr || "";

const mergeCause = (cases: any[], cause: any) => {
  if (!cause) return cases;
  const nextKey = getCauseKey(cause);
  if (!nextKey) return cases;
  let found = false;
  const updated = cases.map((item: any) => {
    const isSame = getCauseKey(item) === nextKey || item?.cnr === cause?.cnr;
    if (!isSame) return item;
    found = true;
    return { ...item, ...cause };
  });
  if (found) return updated;
  return [...updated, cause];
};

const removeCause = (cases: any[], payload: any) => {
  const deletedId = getId(payload?.causeId);
  const deletedCnr = payload?.cnr;
  return cases.filter((cause: any) => {
    if (deletedId && getId(cause?._id) === deletedId) return false;
    if (deletedCnr && cause?.cnr === deletedCnr) return false;
    return true;
  });
};

const updateHearingStatus = (cases: any[], cause: any) => {
  return cases.map((c: any) => {
    if (c.cnr === cause?.previousCnr && c.hearingStatus !== "Passover") {
      return { ...c, hearingStatus: "Completed" };
    }
    if (c.cnr === cause.cnr) {
      return { ...c, hearingStatus: "Active" };
    }
    return c;
  });
};

const isTodaysCause = (cause: any) => {
  return formatDisplayDate(cause?.hearingDate) === getTodayDate();
};

function getStatus(
  cause: any,
  nextCnr: string | null,
  streamStatus?: StreamStatus,
  liveCnr?: string,
): CauseStatus {
  const isLiveForThisCause =
    liveCnr &&
    cause.cnr === liveCnr &&
    (streamStatus === "live" ||
      streamStatus === "paused" ||
      streamStatus === "rec");

  if (isLiveForThisCause) return "running";

  if (cause.hearingStatus === "Completed") return "done";
  if (cause.hearingStatus === "Passover") return "passover";
  if (cause.hearingStatus === "Active") return "active";

  if (nextCnr && cause.cnr === nextCnr) return "next";
  return "waiting";
}

type CauseStatus =
  | "running"
  | "active"
  | "next"
  | "waiting"
  | "passover"
  | "done";
// Same palette as judge cause list (MeetingCauseList)
const STATUS_STYLES: Record<
  CauseStatus,
  { border: string; bg: string; text: string; animation?: string }
> = {
  running: {
    border: "border-blue-400",
    bg: "bg-blue-50",
    text: "text-blue-700",
    animation: "live-bg-pulse",
  },
  active: {
    border: "border-blue-400",
    bg: "bg-blue-50",
    text: "text-blue-700",
  },
  next: {
    border: "border-yellow-300",
    bg: "bg-yellow-50",
    text: "text-yellow-900",
  },
  waiting: {
    border: "border-yellow-300",
    bg: "bg-yellow-50",
    text: "text-yellow-900",
  },
  passover: {
    border: "border-amber-300",
    bg: "bg-amber-100",
    text: "text-amber-900",
  },
  done: {
    border: "border-green-300",
    bg: "bg-green-100",
    text: "text-green-800",
  },
};

function useAutoScroll(
  ref: React.RefObject<HTMLDivElement | null>,
  deps: any[],
  options?: { speed?: number; edgePauseMs?: number },
) {
  const { speed = 24, edgePauseMs = 1800 } = options || {};

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.style.scrollBehavior = "auto";

    const maxScroll = el.scrollHeight - el.clientHeight;

    if (maxScroll <= 2) return;

    // Start from BOTTOM
    el.scrollTop = 0;

    let pausedUntil = Date.now() + edgePauseMs;
    const STEP_MS = 30;

    const intervalId = window.setInterval(() => {
      const now = Date.now();

      if (now < pausedUntil) return;

      const delta = (speed * STEP_MS) / 1000;
      // TOP -> BOTTOM

      el.scrollTop += delta;

      const currentMax = el.scrollHeight - el.clientHeight;

      if (el.scrollTop >= currentMax) {
        el.scrollTop = currentMax;

        // Pause at BOTTOM
        pausedUntil = now + edgePauseMs;

        // Then jump directly back to TOP
        window.setTimeout(() => {
          if (el) {
            el.scrollTop = 0;
          }
        }, edgePauseMs);
      }
    }, STEP_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, deps);
}

export default function PublicCauseList({
  courtRoomId,
  onActiveCauseChange,
  onNeighborsChange,
  onRoomInfoChange,
  streamStatus,
  liveCnr,
}: Readonly<Props>) {
  const [causes, setCauses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingRef = useRef(false);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTime = useRef(0);
  const initialLoadDone = useRef(false);
  const [serverOffsetMs, setServerOffsetMs] = useState(0);

  const refreshTimes = useCallback(async () => {
    try {
      const res = await getPublicPrejoinCasesApi({
        page: 1,
        limit: 20,
        roomId: courtRoomId,
      });
      const fresh: any[] = res.data?.data ?? [];
      if (res.data?.serverNow) {
        setServerOffsetMs(res.data.serverNow - Date.now());
      }
      logger.log(
        "[TIMER][FE][TV] refreshTimes",
        fresh.map((c: any) => ({
          cnr: c.cnr,
          streamStartAt: c.streamStartAt,
          streamEndAt: c.streamEndAt,
          videoDuration: c.videoDuration,
          hearingStatus: c.hearingStatus,
        })),
      );
      setCauses((prev) =>
        prev.map((item) => {
          const f = fresh.find((x: any) => x.cnr === item.cnr);
          return f
            ? {
                ...item,
                caseStartAt: f.caseStartAt,
                caseEndAt: f.caseEndAt,
                streamStartAt: f.streamStartAt,
                streamEndAt: f.streamEndAt,
                videoDuration: f.videoDuration,
                hearingStatus: f.hearingStatus,
              }
            : item;
        }),
      );
    } catch (e) {
      logger.error("[TIMER][FE][TV] refreshTimes failed", e);
    }
  }, [courtRoomId]);

  const fetchCauses = useCallback(
    async (pageNum: number) => {
      if (isFetchingRef.current) return;
      if (!hasMoreRef.current && pageNum !== 1) return;
      isFetchingRef.current = true;
      pageNum === 1 ? setLoading(true) : setIsFetchingMore(true);
      try {
        const res = await getPublicPrejoinCasesApi({
          page: pageNum,
          limit: 20,
          roomId: courtRoomId,
        });
        if (res.data?.serverNow) {
          setServerOffsetMs(res.data.serverNow - Date.now());
        }
        const newData: any[] = res.data?.data ?? [];
        const todayCauses = Array.isArray(newData)
          ? newData.filter(isTodaysCause)
          : [];
        const hasMore: boolean = res.data?.pagination?.hasMore ?? false;
        if (pageNum === 1 && res.data?.roomInfo) {
          onRoomInfoChange?.(res.data.roomInfo);
        }
        pageRef.current = pageNum;
        hasMoreRef.current = hasMore;
        setCauses((prev) => {
          if (pageNum === 1) return todayCauses;

          const seen = new Set(prev.map((c: any) => getCauseKey(c)));

          return [
            ...prev,
            ...todayCauses.filter((c: any) => !seen.has(getCauseKey(c))),
          ];
        });
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
        setIsFetchingMore(false);
      }
    },
    [courtRoomId, onRoomInfoChange],
  );

  useEffect(() => {
    if (!courtRoomId || initialLoadDone.current) return;
    initialLoadDone.current = true;
    void fetchCauses(1);
  }, [courtRoomId, fetchCauses]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTime.current < 300) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight < scrollHeight - 20) return;
      if (isFetchingRef.current || !hasMoreRef.current) return;
      lastScrollTime.current = now;
      void fetchCauses(pageRef.current + 1);
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [fetchCauses]);

  useEffect(() => {
    if (!socket) return;

    const handleCaseSelected = (cause: any) => {
      setCauses((prev) => updateHearingStatus(prev, cause));
      // Case start/end written to DB before "case-selected" emit
      void refreshTimes();
    };

    const handleCauseListCreated = (payload: any) => {
      const cause = payload?.cause || payload;
      const eventCourtRoomId =
        getId(payload?.courtRoomId) || getCauseRoomId(cause);
      if (
        eventCourtRoomId &&
        courtRoomId &&
        eventCourtRoomId !== getId(courtRoomId)
      ) {
        return;
      }
      if (!isTodaysCause(cause)) return;
      setCauses((prev) => mergeCause(prev, cause));
    };

    const handleCauseListUpdated = (payload: any) => {
      const cause = payload?.cause || payload;
      const eventCourtRoomId =
        getId(payload?.courtRoomId) || getCauseRoomId(cause);
      const belongsToCurrentRoom =
        !eventCourtRoomId ||
        !courtRoomId ||
        eventCourtRoomId === getId(courtRoomId);
      const shouldShow = belongsToCurrentRoom && isTodaysCause(cause);
      setCauses((prev) =>
        shouldShow ? mergeCause(prev, cause) : removeCause(prev, cause),
      );
    };

    const handleCauseListDeleted = (payload: any) => {
      const eventCourtRoomId = getId(payload?.courtRoomId);
      if (
        eventCourtRoomId &&
        courtRoomId &&
        eventCourtRoomId !== getId(courtRoomId)
      ) {
        return;
      }
      setCauses((prev) => removeCause(prev, payload));
    };

    const handleHearingStatusUpdated = (payload: any) => {
      if (
        payload?.courtRoomId &&
        courtRoomId &&
        getId(payload.courtRoomId) !== getId(courtRoomId)
      )
        return;
      setCauses((prev) =>
        prev.map((item) =>
          item?.cnr === payload?.cnr
            ? { ...item, hearingStatus: payload.hearingStatus }
            : item,
        ),
      );
      // Completed / Passover stamps caseEndAt — pull it so the timer stops on final time
      void refreshTimes();
    };

    socket.on("case-selected", handleCaseSelected);
    socket.on("hearing-status-updated", handleHearingStatusUpdated);
    socket.on("cause-list-created", handleCauseListCreated);
    socket.on("cause-list-updated", handleCauseListUpdated);
    socket.on("cause-list-deleted", handleCauseListDeleted);
    return () => {
      socket.off("case-selected", handleCaseSelected);
      socket.off("hearing-status-updated", handleHearingStatusUpdated);
      socket.off("cause-list-created", handleCauseListCreated);
      socket.off("cause-list-updated", handleCauseListUpdated);
      socket.off("cause-list-deleted", handleCauseListDeleted);
    };
  }, [courtRoomId, refreshTimes]);

  useEffect(() => {
    if (!recordingSocket) return;
    const onStatus = (d: any) => {
      logger.log("[TIMER][FE][TV] stream-status", d);
      if (
        d?.status === "live" ||
        d?.status === "scheduled" ||
        d?.status === "draining"
      ) {
        void refreshTimes();
      }
    };
    const onStop = () => {
      void refreshTimes();
    };
    recordingSocket.on("stream-status", onStatus);
    recordingSocket.on("stream_stop", onStop);
    return () => {
      recordingSocket.off("stream-status", onStatus);
      recordingSocket.off("stream_stop", onStop);
    };
  }, [refreshTimes]);

  const safeCauses = Array.isArray(causes) ? causes : [];

  const runningIndex = safeCauses.findIndex((c) => {
    if (c.hearingStatus === "Active") return true;
    if (
      liveCnr &&
      c.cnr === liveCnr &&
      (streamStatus === "live" || streamStatus === "paused")
    ) {
      return true;
    }
    return false;
  });

  const prevCause = runningIndex > 0 ? safeCauses[runningIndex - 1] : null;
  // Next = first pending case after the running one (skip Completed / Passover),
  // then wrap to earlier pending ones
  const isPending = (c: any) =>
    c.hearingStatus !== "Completed" &&
    c.hearingStatus !== "Passover" &&
    c.hearingStatus !== "Active" &&
    c.cnr !== safeCauses[runningIndex]?.cnr;
  const nextCauseObj =
    runningIndex !== -1
      ? (safeCauses.slice(runningIndex + 1).find(isPending) ??
        safeCauses.slice(0, runningIndex).find(isPending) ??
        null)
      : null;
  const nextCnr = nextCauseObj?.cnr || null;

  useEffect(() => {
    if (!onActiveCauseChange) return;

    const active =
      safeCauses.find((c) => c.hearingStatus === "Active") ||
      (liveCnr && (streamStatus === "live" || streamStatus === "paused")
        ? safeCauses.find((c) => c.cnr === liveCnr)
        : undefined);

    onActiveCauseChange(
      active
        ? {
            cnr: active.cnr,
            petitioner: active.petitioner,
            respondent: active.respondent,
          }
        : null,
    );
  }, [causes, liveCnr, streamStatus]);

  useEffect(() => {
    if (!onNeighborsChange) return;
    onNeighborsChange({
      prev: prevCause
        ? {
            cnr: prevCause.cnr,
            petitioner: prevCause.petitioner,
            respondent: prevCause.respondent,
          }
        : null,
      next: nextCauseObj
        ? {
            cnr: nextCauseObj.cnr,
            petitioner: nextCauseObj.petitioner,
            respondent: nextCauseObj.respondent,
          }
        : null,
    });
  }, [causes, liveCnr, streamStatus]);

  const entriesWithStatus = safeCauses.map((cause, index) => ({
    cause,
    index,
    status: getStatus(cause, nextCnr, streamStatus, liveCnr),
  }));

  const renderCauseCard = (
    cause: any,
    displayIndex: number,
    status: CauseStatus,
  ) => {
    const s = STATUS_STYLES[status];
    return (
      <div
        key={`${getCauseKey(cause)}-${displayIndex}`}
        className={`p-1 sm:p-2 border-2 rounded-lg transition-all duration-500 ${
          s.border
        } ${s.bg} ${s.animation || ""}`}
      >
        <div className="flex items-center justify-between mb-0 gap-0">
          <span
            className={`text-sm sm:text-base lg:text-3xl font-medium truncate ${s.text}`}
          >
            <b>{displayIndex + 1}. </b>
            {cause.cnr}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <HearingTimer
              cnr={cause.cnr}
              startAt={getHearingTimes(cause).startAt}
              endAt={getHearingTimes(cause).endAt}
              videoDuration={cause.videoDuration}
              isActive={cause.hearingStatus === "Active"}
              serverOffsetMs={serverOffsetMs}
              className="text-xs sm:text-sm lg:text-2xl"
            />
            {status === "running" && (
              <Radio
                size={18}
                className="text-blue-600 shrink-0 animate-pulse"
              />
            )}
          </div>
        </div>
        <div className="text-xs sm:text-sm lg:text-3xl text-gray-700 font-medium">
          {cause.petitioner ||
            cause.parties?.find((p: any) => p.type === "petitioner")?.name ||
            ""}{" "}
          vs{" "}
          {cause.respondent ||
            cause.parties?.find((p: any) => p.type === "respondent")?.name ||
            ""}
        </div>
      </div>
    );
  };

  useAutoScroll(scrollContainerRef, [entriesWithStatus.length, courtRoomId]);

  return (
    <div className="w-full h-full flex flex-col bg-white">
      <div className="sticky top-0 z-10 p-2 sm:p-5 border-b border-gray-200 bg-linear-to-r from-blue-50 to-blue-100">
        <h3 className="font-bold text-blue-900 flex items-center text-base sm:text-lg lg:text-2xl">
          <FileText size={20} className="mr-2 shrink-0" />
          <span className="truncate">CAUSE LIST</span>
        </h3>
      </div>

      <div className="relative flex-1 min-h-0">
        <div
          className="space-y-1 sm:space-y-2 p-1 sm:p-2 overflow-y-auto h-full"
          ref={scrollContainerRef}
        >
          {entriesWithStatus.map(({ cause, index, status }) =>
            renderCauseCard(cause, index, status),
          )}

          {(loading || isFetchingMore) && (
            <div className="text-center text-sm text-gray-400 py-2">
              Loading...
            </div>
          )}

          {!hasMoreRef.current && causes.length > 0 && (
            <div className="text-center text-sm text-gray-400 py-3">
              {`${causes.length} case${causes.length > 1 ? "s" : ""} loaded`}
            </div>
          )}

          {!loading && causes.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-6">
              No cause list found for this courtroom today.
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent" />
      </div>
    </div>
  );
}
